import { APIQueryResult, useAPIQuery } from 'shared/api/APIQuery';
import {
  OnboardingPacketsCountDTO,
  onboardingPacketsCountSchema,
  OnboardingPacketStatus,
} from './OnboardingPacketDTO';
import { APIResponse } from 'shared/api/CarrierAPIClient';
import { useFlag } from 'shared/settings/FeatureToggles';
import { requestFraudMonitorAPI } from 'shared/api/FraudMonitorAPIClient';
import { getCarrierGuid } from 'shared/auth/AuthToken';
import { trackEvent } from 'shared/helpers/Analytics';
import { FIVE_MINUTES } from 'shared/constants/NumberConstants';

export function useOnboardingPacketsCount({
  enabled,
  status,
}: {
  enabled?: boolean;
  status?: OnboardingPacketStatus;
} = {}): APIQueryResult<OnboardingPacketsCountDTO> {
  return useAPIQuery(
    ['onboarding-packets', 'count', { status }],
    () =>
      requestFraudMonitorAPI<APIResponse<OnboardingPacketsCountDTO>>([
        'GET /api/v1/carrier-onboarding/carrier/packets/count{?status}',
        { status },
      ]).then((response) => {
        if (response.data.pending > 0) {
          trackEvent('Carrier Received Onboarding Packets');
        }
        return response;
      }),
    {
      enabled: enabled && !!getCarrierGuid(),
      schema: onboardingPacketsCountSchema,
      staleTime: FIVE_MINUTES,
    },
  );
}

export function useHasOnboardingPackets(): {
  hasOnboardingPackets: boolean;
  isLoading: boolean;
} {
  const isOnboardingPacketsEnabled = useFlag('carrier_onboarding_packets');
  const { data: counts, isLoading } = useOnboardingPacketsCount({
    enabled: isOnboardingPacketsEnabled,
  });

  return {
    hasOnboardingPackets: isOnboardingPacketsEnabled && !!counts?.total,
    isLoading: isOnboardingPacketsEnabled && isLoading,
  };
}

export function useHasPendingOnboardingPackets(): boolean {
  const isOnboardingPacketsEnabled = useFlag('carrier_onboarding_packets');
  const { data: counts } = useOnboardingPacketsCount({
    enabled: isOnboardingPacketsEnabled,
  });

  return isOnboardingPacketsEnabled && !!counts?.pending;
}
